import { Button } from '@mantine/core';
import {
  CalendarBlankIcon,
  EnvelopeSimpleIcon,
  EyeIcon,
  NotePencilIcon,
} from '@phosphor-icons/react';

import { useCardScheduleByAnswerId } from 'hooks/useCardSchedule';
import TRACKING_IDS from 'tracking-ids';

import type { Icon } from '@phosphor-icons/react';
import type { CitationSource } from 'modules/shared/libs/chat/types';
import type { ReactNode } from 'react';

// Compact action button used in the per-answer hover row (Copy, Sources,
// Create Card, View Card, Schedule, Add to Digest, Export). Kept small so
// the button row stays consistent across all message actions.
type ActionButtonProps = {
  icon: Icon;
  label: string;
  onClick?: () => void;
  trackingId?: string;
};

export function ActionButton({
  icon: IconComponent,
  label,
  onClick,
  trackingId,
}: ActionButtonProps): ReactNode {
  return (
    <Button
      variant="transparent"
      color="blue"
      size="compact-sm"
      leftSection={<IconComponent size={16} />}
      onClick={onClick}
      title={label}
      data-tracking-id={trackingId}
    >
      {label}
    </Button>
  );
}

// Handler signature shared by Create Card, Add to Digest, Export — all take
// the message id + answer text + citations + originating user question.
type AnswerActionHandler = (
  messageId: string,
  messageText: string,
  sources: CitationSource[],
  userQuestion?: string,
) => void;

type CardActionsProps = {
  messageId: string;
  messageText: string;
  sources: CitationSource[];
  userQuestion?: string;
  // Create Card and Add to Digest are the two "produce something new" actions
  // — visible independently. Create Card is gated on the answer having no
  // linked card yet (see effectiveLinkedCardId below); Add to Digest is
  // always visible when provided (materializes a digest item directly from
  // the answer, independent of any linked card).
  onCreateCard?: AnswerActionHandler;
  onAddToDigest?: AnswerActionHandler;
  // View Card and Schedule are the two "act on the linked card" actions —
  // visible only when a card has been linked to this answer.
  linkedCardId?: number;
  onViewCard?: (cardId: number) => void;
  onScheduleCard?: (cardId: number) => void;
};

export function CardActions({
  messageId,
  messageText,
  sources,
  userQuestion,
  onCreateCard,
  onAddToDigest,
  linkedCardId,
  onViewCard,
  onScheduleCard,
}: CardActionsProps): ReactNode {
  // Always query when onCreateCard is set (mirrors ResearchChat) so that
  // cache invalidation from card deletion/unlink flips the action bar back
  // from "View Card" to "Create Card". Persisted result wins over the
  // in-memory linkedCardId fallback.
  const { linkedCardId: persistedLinkedCardId } = useCardScheduleByAnswerId(
    onCreateCard ? messageId : undefined,
  );
  const effectiveLinkedCardId = persistedLinkedCardId ?? linkedCardId;

  return (
    <>
      {onCreateCard && !effectiveLinkedCardId && (
        <ActionButton
          icon={NotePencilIcon}
          label="Create Card"
          trackingId={TRACKING_IDS.askKlue.answer.createCardButton}
          onClick={() =>
            onCreateCard(messageId, messageText, sources, userQuestion)
          }
        />
      )}
      {effectiveLinkedCardId && onViewCard && (
        <ActionButton
          icon={EyeIcon}
          label="View Card"
          trackingId={TRACKING_IDS.askKlue.answer.viewCardButton}
          onClick={() => onViewCard(effectiveLinkedCardId)}
        />
      )}
      {effectiveLinkedCardId && onScheduleCard && (
        <ActionButton
          icon={CalendarBlankIcon}
          label="Schedule"
          trackingId={TRACKING_IDS.askKlue.answer.scheduleCardButton}
          onClick={() => onScheduleCard(effectiveLinkedCardId)}
        />
      )}
      {onAddToDigest && (
        <ActionButton
          icon={EnvelopeSimpleIcon}
          label="Add to Digest"
          trackingId={TRACKING_IDS.askKlue.answer.addToDigestButton}
          onClick={() =>
            onAddToDigest(messageId, messageText, sources, userQuestion)
          }
        />
      )}
    </>
  );
}
